import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FAMILIES, FAMILY_ORDER, NETWORK_GROUPS, NETWORKS, groupOfNetwork, networksByFamily } from "../../data/apiReference";
import { providersForNetwork, serviceById } from "../../data/services";
import { useDemoKey } from "../../lib/demoKey";
import { NETWORKS_PATH, networkPath, useNetworkRoute } from "../../lib/networkRoute";
import { scrollToId } from "./DocBlocks";
import ChainLogo from "./ChainLogo";
import NetworkSelect from "./NetworkSelect";
import { implLabel, implSections } from "./implMeta";

// Anchors that live on the overview page (/networks).
const GETTING_STARTED = [
  { id: "overview", label: "Overview" },
  { id: "authentication", label: "Authentication" },
  { id: "endpoints", label: "Endpoints & headers" },
  { id: "quickstart", label: "Quickstart & SDKs" },
  { id: "networks", label: "Supported networks" },
];
const REFERENCE = [
  { id: "websocket", label: "WebSocket" },
  { id: "grpc", label: "gRPC streams" },
  { id: "limits", label: "Rate limits & plans" },
  { id: "errors", label: "Errors & ref codes" },
];

const Chevron = ({ open }) => (
  <svg viewBox="0 0 20 20" className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Label = ({ children, className = "" }) => (
  <p className={`m-0 mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 ${className}`}>{children}</p>
);

const anchorButtonClass =
  "block w-full rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-sm text-gray-400 transition-colors hover:bg-white/[0.04] hover:text-white";

// One network (chain id) of the selected ecosystem: "Mainnet · 1", "Sepolia · 11155111".
const NetworkLink = ({ network, onNavigate, showName = false }) => (
  <NavLink
    to={networkPath(network.slug)}
    onClick={onNavigate}
    className={({ isActive }) =>
      `group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm no-underline transition-colors ${
        isActive ? "bg-primary/15 text-primary" : "text-gray-400 hover:bg-white/[0.04] hover:text-white"
      }`
    }
  >
    {showName ? (
      <ChainLogo chain={{ name: network.name, logo: network.logo, family: network.family }} size={20} />
    ) : (
      <span
        className="ml-1 h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: network.env === "mainnet" ? FAMILIES[network.family].accent : "rgba(255,255,255,0.35)" }}
      />
    )}
    <span className="truncate">{showName ? network.fullName : network.label}</span>
    {network.chainId !== undefined ? (
      <span className="ml-auto truncate rounded px-1 py-px font-mono text-[9px] text-gray-500 ring-1 ring-white/10" title={`chain id ${network.chainId}`}>
        {String(network.chainId)}
      </span>
    ) : (
      network.env === "testnet" && <span className="ml-auto rounded px-1 py-px font-mono text-[9px] uppercase text-gray-500 ring-1 ring-white/10">test</span>
    )}
  </NavLink>
);

const ApiSidebar = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { key, ready } = useDemoKey();
  const { network: activeNetwork, impl, setImpl } = useNetworkRoute();

  // The selected ecosystem is derived from the route, so links are shareable.
  const selectedGroup = groupOfNetwork(activeNetwork);
  const activeFamily = activeNetwork?.family || null;
  const providers = activeNetwork ? providersForNetwork(activeNetwork.slug) : null;

  const [openFamilies, setOpenFamilies] = useState(() => new Set(activeFamily ? [activeFamily] : ["evm"]));
  React.useEffect(() => {
    if (activeFamily) setOpenFamilies((prev) => new Set(prev).add(activeFamily));
  }, [activeFamily]);

  const toggleFamily = (id) =>
    setOpenFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const goAnchor = (id) => {
    onNavigate?.();
    if (location.pathname === NETWORKS_PATH) scrollToId(id);
    else navigate(NETWORKS_PATH, { state: { anchor: id } });
  };

  // Selecting an ecosystem jumps to its first network; "All networks" returns to the catalog.
  const selectGroup = (groupId) => {
    onNavigate?.();
    if (!groupId) {
      navigate(NETWORKS_PATH, { state: { anchor: "networks" } });
      return;
    }
    const group = NETWORK_GROUPS.find((g) => g.id === groupId);
    if (group) navigate(networkPath(group.networks[0].slug));
  };

  const goSection = (id) => {
    onNavigate?.();
    scrollToId(id);
  };

  // Services covering the active network → page sections (Alchemy-style sub-nav).
  const serviceItems = useMemo(() => {
    if (!activeNetwork) return [];
    const items = [
      {
        id: "datanodes",
        service: serviceById("datanodes"),
        children: activeNetwork.chains.map((c) => ({ slug: c.slug, label: implLabel(c), active: impl?.slug === c.slug })),
      },
    ];
    if (providers?.nodegate) items.push({ id: "nodegate", service: serviceById("nodegate"), note: providers.nodegate.detail });
    if (providers?.metrics) items.push({ id: "metrics", service: serviceById("chaingraph"), note: `${providers.metrics.name} · ${providers.metrics.detail}` });
    return items;
  }, [activeNetwork, impl, providers]);

  const onThisPage = useMemo(() => (impl ? implSections(impl) : []), [impl]);

  return (
    <nav aria-label="Networks reference" className="text-sm">
      {/* Demo key status */}
      <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <Label>Examples</Label>
        <p className="m-0 -mt-1 px-2 text-xs leading-relaxed text-gray-300">
          {ready && key ? (
            <>
              Run as-is with the shared <span className="text-primary">public demo key</span> (Free tier, rate-limited).
            </>
          ) : ready ? (
            <>
              Replace <span className="font-mono text-white">YOUR_API_KEY</span> with a key from the dashboard.
            </>
          ) : (
            "Loading demo key…"
          )}
        </p>
      </div>

      <Label>Getting started</Label>
      <ul className="m-0 mb-6 list-none space-y-0.5 p-0">
        {GETTING_STARTED.map((item) => (
          <li key={item.id}>
            <button type="button" onClick={() => goAnchor(item.id)} className={anchorButtonClass}>
              {item.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Network selector */}
      <div className="mb-6 border-t border-white/10 pt-5">
        <Label>Networks</Label>
        <NetworkSelect value={selectedGroup?.id || null} onChange={selectGroup} />

        {selectedGroup ? (
          <div className="mt-4">
            {/* Networks of the ecosystem, one per chain id */}
            <p className="m-0 mb-2 px-2 text-sm font-bold text-primary">{selectedGroup.name}</p>
            <ul className="m-0 list-none space-y-0.5 p-0">
              {selectedGroup.networks.map((n) => (
                <li key={n.slug}>
                  <NetworkLink network={n} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>

            {/* API reference available on the active network */}
            {activeNetwork && serviceItems.length > 0 && (
              <>
                <Label className="mt-4 mb-1">API reference</Label>
                <ul className="m-0 list-none space-y-0.5 border-l border-white/10 p-0 pl-2">
                  {serviceItems.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => goSection(item.id)}
                        className="flex w-full items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-[13px] font-semibold text-gray-200 transition-colors hover:bg-white/[0.04] hover:text-white"
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: item.service.accent }} />
                        <span className="truncate">{item.service.name.split(" · ")[0]}</span>
                        {item.children && <span className="ml-auto font-mono text-[10px] font-normal text-gray-500">{item.children.length}</span>}
                      </button>
                      {item.note && <p className="m-0 mb-1 px-2 pl-6 text-[11px] text-gray-500">{item.note}</p>}
                      {item.children && (
                        <ul className="m-0 mb-1 list-none space-y-0.5 p-0 pl-4">
                          {item.children.map((c) => (
                            <li key={c.slug}>
                              <button
                                type="button"
                                onClick={() => {
                                  onNavigate?.();
                                  setImpl(c.slug);
                                  scrollToId("datanodes");
                                }}
                                className={`flex w-full items-center gap-2 rounded-lg border-0 px-2 py-1 text-left text-[13px] transition-colors ${
                                  c.active ? "bg-primary/15 text-primary" : "bg-transparent text-gray-400 hover:bg-white/[0.04] hover:text-white"
                                }`}
                              >
                                <span className="truncate">{c.label}</span>
                                <span className="ml-auto truncate font-mono text-[9px] text-gray-500">{c.slug}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {onThisPage.length > 0 && (
              <>
                <Label className="mt-4 mb-1">On this page</Label>
                <ul className="m-0 list-none space-y-0.5 border-l border-white/10 p-0 pl-2">
                  {onThisPage.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => goSection(s.id)}
                        className="block w-full rounded-lg border-0 bg-transparent px-2 py-1 text-left text-[13px] text-gray-400 transition-colors hover:bg-white/[0.04] hover:text-white"
                      >
                        {s.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <button
              type="button"
              onClick={() => selectGroup(null)}
              className="mt-4 block w-full rounded-lg border border-white/10 bg-transparent px-2 py-1.5 text-left text-xs text-gray-400 transition-colors hover:border-white/30 hover:text-white"
            >
              ← All {NETWORKS.length} networks
            </button>
          </div>
        ) : (
          <ul className="m-0 mt-4 list-none space-y-1 p-0">
            {FAMILY_ORDER.map((fid) => {
              const fam = FAMILIES[fid];
              const list = networksByFamily(fid);
              if (list.length === 0) return null;
              const open = openFamilies.has(fid);
              return (
                <li key={fid}>
                  <button
                    type="button"
                    onClick={() => toggleFamily(fid)}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-sm font-semibold text-gray-300 transition-colors hover:bg-white/[0.04]"
                  >
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: fam.accent }} />
                      {fam.name}
                    </span>
                    <span className="flex items-center gap-2 text-gray-500">
                      <span className="font-mono text-[10px]">{list.length}</span>
                      <Chevron open={open} />
                    </span>
                  </button>
                  {open && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                      {list.map((n) => (
                        <NetworkLink key={n.slug} network={n} onNavigate={onNavigate} showName />
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Label>Reference</Label>
      <ul className="m-0 list-none space-y-0.5 p-0">
        {REFERENCE.map((item) => (
          <li key={item.id}>
            <button type="button" onClick={() => goAnchor(item.id)} className={anchorButtonClass}>
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default ApiSidebar;
